import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { academicAPI, assessmentAPI, reportCardAPI } from '../../services/api';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';
import AssessmentConfigurationFormDialog from './forms/AssessmentConfigurationFormDialog';
import AssessmentTypeFormDialog from './forms/AssessmentTypeFormDialog';
import BulkAssessmentConfigDialog from './forms/BulkAssessmentConfigDialog';


const extractCollection = (responseOrPayload) => {
  const payload = responseOrPayload?.data !== undefined ? responseOrPayload.data : responseOrPayload;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const getErrorMessage = (error, fallback = 'Operation failed') => (
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  fallback
);

const getSubjectName = (subject) => subject?.subject_name || subject?.name || '-';

const getClassStreamPaperLabel = (assignment) => {
  const className = assignment?.class?.name || 'Class N/A';
  const streamName = assignment?.stream?.name || 'No Stream';
  const subjectName = getSubjectName(assignment?.subject_paper?.subject);
  const paperName = assignment?.subject_paper?.paper_name || '';
  const paperCode = assignment?.subject_paper?.paper_code || '';
  return `${className} - ${streamName} | ${subjectName}${paperName ? ` - ${paperName}` : ''}${paperCode ? ` (${paperCode})` : ''}`;
};

const buildConfigurationContext = (config) => {
  const className = config?.class_stream_subject_paper?.class?.name || 'Class N/A';
  const streamName = config?.class_stream_subject_paper?.stream?.name || 'No Stream';
  const subjectName = getSubjectName(config?.class_stream_subject_paper?.subject_paper?.subject);
  const paperName = config?.class_stream_subject_paper?.subject_paper?.paper_name || '';
  return `${className} - ${streamName} | ${subjectName}${paperName ? ` - ${paperName}` : ''}`;
};

const INITIAL_FORM = {
  class_stream_subject_paper_id: '',
  assessment_type_id: '',
  assessment_component: 'examination',
  academic_year: new Date().getFullYear(),
  term_id: '',
  max_marks: 100,
  weight_percentage: '',
  sequence_order: '',
  due_date: '',
  is_compulsory: true,
  include_in_final: true,
  is_active: true
};

const TYPE_CATEGORIES = ['Continuous Assessment', 'Examination', 'Project', 'Practical', 'Assignment'];

const INITIAL_TYPE_FORM = {
  assessment_code: '',
  assessment_name: '',
  short_name: '',
  category: '',
  is_active: true
};

const AssessmentConfigurations = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.ASSESSMENTS.CONFIGURATIONS, ACTIONS.READ);
  const canCreate = can(RESOURCES.ASSESSMENTS.CONFIGURATIONS, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.ASSESSMENTS.CONFIGURATIONS, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.ASSESSMENTS.CONFIGURATIONS, ACTIONS.DELETE);
  const [configurations, setConfigurations] = useState([]);
  const [allConfigurations, setAllConfigurations] = useState([]);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [classStreamSubjectPapers, setClassStreamSubjectPapers] = useState([]);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingType, setSavingType] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showTypeForm, setShowTypeForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [showBulkEditForm, setShowBulkEditForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [typeFormData, setTypeFormData] = useState(INITIAL_TYPE_FORM);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 15, total: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [termFilter, setTermFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  const sortedClassStreamSubjectPapers = useMemo(
    () => [...classStreamSubjectPapers].sort((a, b) => getClassStreamPaperLabel(a).localeCompare(getClassStreamPaperLabel(b))),
    [classStreamSubjectPapers]
  );

  const fetchAllConfigurations = useCallback(async () => {
    try {
      const response = await assessmentAPI.getAssessmentConfigurations({ per_page: 10000 });
      setAllConfigurations(extractCollection(response));
    } catch { setAllConfigurations([]); }
  }, []);

  const existingConfigKeys = useMemo(() => {
    const s = new Set();
    allConfigurations.forEach((c) => s.add(`${c.class_stream_subject_paper_id}-${c.assessment_type_id}-${c.term_id}-${c.academic_year}`));
    return s;
  }, [allConfigurations]);

  const fetchConfigurations = useCallback(async () => {
    if (!canRead) return;
    try {
      const params = {
        page,
        per_page: pageSize,
        ...(searchTerm && { search: searchTerm }),
        ...(typeFilter && { assessment_type_id: typeFilter }),
        ...(termFilter && { term_id: termFilter }),
        ...(yearFilter && { academic_year: yearFilter }),
        ...(activeFilter !== '' && { is_active: activeFilter })
      };
      const response = await assessmentAPI.getAssessmentConfigurations(params);
      const payload = response?.data ?? response;
      setConfigurations(extractCollection(response));
      setPagination({
        current_page: payload?.current_page ?? payload?.data?.current_page ?? 1,
        last_page: payload?.last_page ?? payload?.data?.last_page ?? 1,
        per_page: payload?.per_page ?? payload?.data?.per_page ?? pageSize,
        total: payload?.total ?? payload?.data?.total ?? 0
      });
    } catch (error) {
      console.error('Error fetching configurations:', error);
      setConfigurations([]);
      setSnackbar({ open: true, message: 'Failed to load assessment configurations', severity: 'error' });
    }
  }, [page, pageSize, searchTerm, typeFilter, termFilter, yearFilter, activeFilter, canRead]);

  const fetchAssessmentTypes = useCallback(async () => {
    try {
      const response = await assessmentAPI.getActiveAssessmentTypes();
      setAssessmentTypes(extractCollection(response));
    } catch (error) {
      setAssessmentTypes([]);
      setSnackbar({ open: true, message: 'Failed to load assessment types', severity: 'error' });
    }
  }, []);

  const fetchClassStreamSubjectPapers = useCallback(async () => {
    try {
      const response = await reportCardAPI.getClassStreamPapers({ per_page: 1000 });
      const papers = extractCollection(response);
      setClassStreamSubjectPapers(papers);
      if (papers.length === 0) {
        setSnackbar({ open: true, message: 'No curriculum found. Please go to Academic Management → Curriculum to assign subject papers to classes first.', severity: 'warning' });
      }
    } catch (error) {
      console.error('fetchClassStreamSubjectPapers error:', error?.response ?? error);
      setClassStreamSubjectPapers([]);
      const errorMsg = error?.response?.data?.message
        || error?.response?.data?.error
        || (error?.response?.status === 403 ? 'Permission denied.' : null)
        || (error?.response?.status === 404 ? 'Curriculum endpoint not found.' : null)
        || `Failed to load curriculum data (${error?.response?.status ?? 'network error'}).`;
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    }
  }, []);

  const fetchTerms = useCallback(async () => {
    try {
      const response = await academicAPI.getTerms();
      const fetched = extractCollection(response);
      setTerms(fetched);
      const current = fetched.find((t) => t.is_current) || fetched[0];
      if (current) setFormData((prev) => ({
        ...prev,
        term_id: current.id,
        due_date: current.end_date ? String(current.end_date).slice(0, 10) : prev.due_date,
      }));
    } catch (error) {
      setTerms([]);
      setSnackbar({ open: true, message: 'Failed to load terms', severity: 'error' });
    }
  }, []);

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchConfigurations(), fetchAllConfigurations(), fetchAssessmentTypes(), fetchTerms(), fetchClassStreamSubjectPapers()]);
    } finally {
      setLoading(false);
    }
  }, [fetchConfigurations, fetchAllConfigurations, fetchAssessmentTypes, fetchClassStreamSubjectPapers, fetchTerms]);

  useEffect(() => { fetchInitialData(); }, [fetchInitialData]);
  useEffect(() => { setPage(1); }, [searchTerm, typeFilter, termFilter, yearFilter, activeFilter]);

  const validateForm = () => {
    const academicYear = Number(formData.academic_year);
    const maxMarks = Number(formData.max_marks);
    const weight = Number(formData.weight_percentage);
    const classStreamSubjectPaperId = Number(formData.class_stream_subject_paper_id);
    const assessmentTypeId = Number(formData.assessment_type_id);
    const termId = Number(formData.term_id);

    if (!classStreamSubjectPaperId || !assessmentTypeId || !termId) {
      setSnackbar({ open: true, message: 'Class-stream paper, assessment type, and term are required', severity: 'error' });
      return null;
    }
    if (!academicYear || Number.isNaN(academicYear) || academicYear < 2020) {
      setSnackbar({ open: true, message: 'Valid academic year is required (2020 or later)', severity: 'error' });
      return null;
    }
    if (Number.isNaN(maxMarks) || maxMarks <= 0 || maxMarks > 999.99) {
      setSnackbar({ open: true, message: 'Max marks must be between 0.01 and 999.99', severity: 'error' });
      return null;
    }
    if (Number.isNaN(weight) || weight < 0 || weight > 100) {
      setSnackbar({ open: true, message: 'Weight must be between 0 and 100', severity: 'error' });
      return null;
    }
    const sequenceOrder = formData.sequence_order === '' || formData.sequence_order === null
      ? null
      : Number(formData.sequence_order);
    if (sequenceOrder !== null && (Number.isNaN(sequenceOrder) || sequenceOrder < 1 || sequenceOrder > 200)) {
      setSnackbar({ open: true, message: 'Sequence order must be between 1 and 200', severity: 'error' });
      return null;
    }
    if (formData.due_date) {
      const dueDate = new Date(formData.due_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dueDate < today) {
        setSnackbar({ open: true, message: 'Due date must be today or in the future', severity: 'error' });
        return null;
      }
    }
    return {
      class_stream_subject_paper_id: classStreamSubjectPaperId,
      assessment_type_id: assessmentTypeId,
      assessment_component: formData.assessment_component || 'examination',
      academic_year: academicYear,
      term_id: termId,
      max_marks: maxMarks,
      weight_percentage: weight,
      sequence_order: sequenceOrder,
      due_date: formData.due_date || null,
      is_compulsory: Boolean(formData.is_compulsory),
      include_in_final: Boolean(formData.include_in_final),
      is_active: Boolean(formData.is_active)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = validateForm();
    if (!payload) return;
    setSaving(true);
    try {
      if (editingConfig) {
        await assessmentAPI.updateAssessmentConfiguration(editingConfig.id, payload);
        setSnackbar({ open: true, message: 'Configuration updated successfully', severity: 'success' });
      } else {
        await assessmentAPI.createAssessmentConfiguration(payload);
        setSnackbar({ open: true, message: 'Configuration created successfully', severity: 'success' });
      }
      await Promise.all([fetchConfigurations(), fetchAllConfigurations()]);
      resetForm();
    } catch (error) {
      if (error?.response?.status === 500 && error?.response?.data?.message?.includes('Duplicate entry')) {
        setSnackbar({ open: true, message: 'This assessment configuration already exists for the selected class, subject, term, and year.', severity: 'error' });
        return;
      }
      const errorMessage = error?.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(', ')
        : getErrorMessage(error);
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      class_stream_subject_paper_id: config.class_stream_subject_paper_id ?? '',
      assessment_type_id: config.assessment_type_id ?? '',
      assessment_component: config.assessment_component ?? 'examination',
      academic_year: config.academic_year ?? new Date().getFullYear(),
      term_id: config.term_id ?? '',
      max_marks: config.max_marks ?? '',
      weight_percentage: config.weight_percentage ?? '',
      sequence_order: config.sequence_order ?? '',
      due_date: config.due_date ? String(config.due_date).slice(0, 10) : '',
      is_compulsory: Boolean(config.is_compulsory),
      include_in_final: Boolean(config.include_in_final ?? true),
      is_active: Boolean(config.is_active)
    });
    setShowForm(true);
  };

  const handleDelete = (id) => setConfirmDelete(id);

  const confirmDeleteAction = async () => {
    try {
      await assessmentAPI.deleteAssessmentConfiguration(confirmDelete);
      setSnackbar({ open: true, message: 'Configuration deleted successfully', severity: 'success' });
      await Promise.all([fetchConfigurations(), fetchAllConfigurations()]);
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error, 'Failed to delete configuration'), severity: 'error' });
    } finally {
      setConfirmDelete(null);
    }
  };

  const resetForm = () => {
    const current = terms.find((t) => t.is_current) || terms[0];
    setFormData({
      ...INITIAL_FORM,
      term_id: current?.id || '',
      due_date: current?.end_date ? String(current.end_date).slice(0, 10) : '',
    });
    setEditingConfig(null);
    setShowForm(false);
  };
  const resetTypeForm = () => { setTypeFormData(INITIAL_TYPE_FORM); setShowTypeForm(false); };

  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    setSavingType(true);
    try {
      const response = await assessmentAPI.createAssessmentType(typeFormData);
      const payload = response?.data;
      const createdType = payload?.data || payload;
      await fetchAssessmentTypes();
      if (createdType?.id) setFormData((prev) => ({ ...prev, assessment_type_id: createdType.id }));
      setSnackbar({ open: true, message: 'Assessment type added successfully', severity: 'success' });
      resetTypeForm();
    } catch (error) {
      setSnackbar({ open: true, message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to add assessment type', severity: 'error' });
    } finally {
      setSavingType(false);
    }
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view assessment configurations.</Alert>;

  return (
    <Box>
      <ModulePageHeader
        title="Assessment Configurations"
        subtitle="Configure assessment weights per class, stream, subject paper, term, and year"
        actions={[
          <Button key="refresh" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchInitialData} disabled={loading} fullWidth={isMobile}>Refresh</Button>,
          ...(canCreate ? [
            <Button key="add-type" variant="outlined" onClick={() => setShowTypeForm(true)} disabled={loading} fullWidth={isMobile}>Add Assessment Type</Button>,
            <Button key="bulk-add" variant="outlined" startIcon={<AddIcon />} onClick={() => setShowBulkForm(true)} disabled={loading} fullWidth={isMobile}>Bulk Add</Button>,
          ] : []),
          ...(canUpdate ? [
            <Button key="bulk-edit" variant="outlined" startIcon={<EditIcon />} onClick={() => setShowBulkEditForm(true)} disabled={loading} fullWidth={isMobile}>Bulk Edit</Button>,
          ] : []),
          ...(canCreate ? [
            <Button key="add" variant="contained" startIcon={<AddIcon />} onClick={() => setShowForm(true)} disabled={loading} fullWidth={isMobile}>Add Configuration</Button>,
          ] : []),
        ]}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth size="small" placeholder="Search..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Autocomplete
                fullWidth size="small"
                options={assessmentTypes}
                getOptionLabel={(o) => o.assessment_name || o.short_name || ''}
                value={assessmentTypes.find(t => t.id === typeFilter) || null}
                onChange={(_, v) => setTypeFilter(v?.id || '')}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Assessment Type" />}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Autocomplete
                fullWidth size="small"
                options={terms}
                getOptionLabel={(t) => `${t.name || ''} ${t.year || ''}`.trim()}
                value={terms.find(t => t.id === termFilter) || null}
                onChange={(_, v) => setTermFilter(v?.id || '')}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Term" />}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth size="small" type="number" label="Year" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <Autocomplete
                fullWidth size="small"
                options={[{ label: 'Active', value: true }, { label: 'Inactive', value: false }]}
                value={activeFilter === '' ? null : { label: activeFilter ? 'Active' : 'Inactive', value: activeFilter }}
                onChange={(_, o) => setActiveFilter(o ? o.value : '')}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                renderInput={(params) => <TextField {...params} label="Status" />}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Box py={5} textAlign="center"><CircularProgress size={28} /></Box>
          ) : configurations.length === 0 ? (
            <EmptyRecordsState message="No assessment configurations found." />
          ) : isMobile ? (
            <ResponsiveRecordList
              items={configurations}
              renderItem={(config) => (
                <Stack spacing={1.25}>
                  <Typography fontWeight={700}>{config.assessment_type?.assessment_name || '-'}</Typography>
                  <Typography variant="body2" color="text.secondary">{buildConfigurationContext(config)}</Typography>
                  <Typography variant="body2" color="text.secondary">{`${config.term?.name || '-'} ${config.academic_year || ''}`.trim()}</Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Max {config.max_marks} | Weight {config.weight_percentage}%</Typography>
                    <Chip label={config.is_active ? 'Active' : 'Inactive'} color={config.is_active ? 'success' : 'default'} size="small" />
                  </Box>
                  <Typography variant="body2" color="text.secondary">Due: {config.due_date ? String(config.due_date).slice(0, 10) : '-'}</Typography>
                  <Box textAlign="right">
                    {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(config)}><EditIcon /></IconButton>}
                    {canDelete && <IconButton size="small" color="error" onClick={() => handleDelete(config.id)}><DeleteIcon /></IconButton>}
                  </Box>
                </Stack>
              )}
            />
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 1080 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Assessment Type</TableCell>
                    <TableCell>Class / Stream / Subject</TableCell>
                    <TableCell>Term</TableCell>
                    <TableCell>Max Marks</TableCell>
                    <TableCell>Weight %</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {configurations.map((config) => (
                    <TableRow key={config.id} hover>
                      <TableCell>{config.assessment_type?.assessment_name || '-'}</TableCell>
                      <TableCell>{buildConfigurationContext(config)}</TableCell>
                      <TableCell>{`${config.term?.name || '-'} ${config.academic_year || ''}`.trim()}</TableCell>
                      <TableCell>{config.max_marks}</TableCell>
                      <TableCell>{config.weight_percentage}%</TableCell>
                      <TableCell>{config.due_date ? String(config.due_date).slice(0, 10) : '-'}</TableCell>
                      <TableCell><Chip label={config.is_active ? 'Active' : 'Inactive'} color={config.is_active ? 'success' : 'default'} size="small" /></TableCell>
                      <TableCell>
                        {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(config)}><EditIcon /></IconButton>}
                        {canDelete && <IconButton size="small" color="error" onClick={() => handleDelete(config.id)}><DeleteIcon /></IconButton>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            pagination={pagination}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(size) => { setPageSize(parseInt(size)); setPage(1); }}
            isMobile={isMobile}
          />
        </CardContent>
      </Card>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Configuration</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this configuration? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      <AssessmentConfigurationFormDialog
        open={showForm}
        onClose={resetForm}
        onSubmit={handleSubmit}
        editingConfig={editingConfig}
        saving={saving}
        formData={formData}
        setFormData={setFormData}
        sortedClassStreamSubjectPapers={sortedClassStreamSubjectPapers}
        assessmentTypes={assessmentTypes}
        terms={terms}
        getClassStreamPaperLabel={getClassStreamPaperLabel}
        existingConfigKeys={existingConfigKeys}
        isMobile={isMobile}
      />

      <BulkAssessmentConfigDialog
        open={showBulkForm}
        onClose={() => setShowBulkForm(false)}
        onDone={(result) => {
          setShowBulkForm(false);
          Promise.all([fetchConfigurations(), fetchAllConfigurations()]);
          const created = result.total - result.skipped - result.errors.length;
          const msg = result.skipped === result.total
            ? 'All configurations already exist for this term.'
            : result.errors.length === 0
            ? `${created} configuration(s) created successfully${result.skipped > 0 ? `, ${result.skipped} already existed` : ''}.`
            : `Done: ${created} created, ${result.skipped} skipped, ${result.errors.length} failed.`;
          const severity = result.skipped === result.total ? 'warning' : result.errors.length > 0 ? 'warning' : 'success';
          setSnackbar({ open: true, message: msg, severity });
        }}
        sortedClassStreamSubjectPapers={sortedClassStreamSubjectPapers}
        assessmentTypes={assessmentTypes}
        terms={terms}
        getClassStreamPaperLabel={getClassStreamPaperLabel}
        existingConfigKeys={existingConfigKeys}
        isMobile={isMobile}
      />

      <BulkAssessmentConfigDialog
        open={showBulkEditForm}
        onClose={() => setShowBulkEditForm(false)}
        onDone={(result) => {
          setShowBulkEditForm(false);
          Promise.all([fetchConfigurations(), fetchAllConfigurations()]);
          const msg = result.errors.length > 0
            ? result.errors[0]
            : `${result.total} configuration(s) updated successfully.`;
          setSnackbar({ open: true, message: msg, severity: result.errors.length > 0 ? 'error' : 'success' });
        }}
        sortedClassStreamSubjectPapers={sortedClassStreamSubjectPapers}
        assessmentTypes={assessmentTypes}
        terms={terms}
        existingConfigKeys={existingConfigKeys}
        allConfigurations={allConfigurations}
        editMode={true}
        isMobile={isMobile}
      />

      <AssessmentTypeFormDialog
        open={showTypeForm}
        onClose={resetTypeForm}
        onSubmit={handleTypeSubmit}
        editingType={null}
        formData={typeFormData}
        setFormData={setTypeFormData}
        categories={TYPE_CATEGORIES}
        isMobile={isMobile}
        saving={savingType}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssessmentConfigurations;
